import React, { useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Stack, Divider, Grid,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import * as XLSX from 'xlsx';
import PopoverFiltroRelatorio from './PopoverFiltroRelatorio';
import generatePdfRelatorioComissaoServico from './EditPdfRelatorioComissaoServico';
import { cores, coresGrafico } from '../styles/styleUtils';

// ── Paleta TriAxis — aliases dos tokens de styleUtils ─────────────────────
const LARANJA  = cores.laranja;
const CINZA    = cores.chumbo;
const LARANJA2 = cores.laranjaEscuro;
const CORES    = coresGrafico;

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ── KPI Card ────────────────────────────────────────────────────────────────
const KpiCard = ({ label, valor, destaque }) => (
  <Box sx={{
    flex: 1, minWidth: 140, textAlign: 'center',
    border: `1px solid ${destaque ? LARANJA : '#DDD'}`,
    borderLeft: `4px solid ${destaque ? LARANJA : CINZA}`,
    borderRadius: 1, p: 1.5,
    backgroundColor: destaque ? '#FFF3E0' : '#FAFAFA',
  }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="subtitle1" fontWeight="bold" color={destaque ? LARANJA : CINZA}>{valor}</Typography>
  </Box>
);

// ── Título de seção ──────────────────────────────────────────────────────────
const SecTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 4 }}>
    <Box sx={{ width: 4, height: 20, backgroundColor: LARANJA, borderRadius: 1 }} />
    <Typography variant="subtitle1" fontWeight="bold" color={CINZA}>{children}</Typography>
  </Box>
);

export default function RelatorioComissaoServico({ data, type, filtro, setFiltro, onLimpar }) {
  const [openFiltro, setOpenFiltro] = useState(null);

  const onCloseFiltro   = () => setOpenFiltro(null);
  const limparFiltro    = () => { setOpenFiltro(false); onLimpar(); };
  const onConfirmFiltro = (f) => { setOpenFiltro(false); setFiltro(f); };

  if (!data) return null;

  const kpis        = data.kpis || { total: 0, qtdVendedores: 0, qtdVendas: 0, ticketMedio: 0 };
  const porTipoServico = data.porTipoServico || [];
  const porVendedor  = data.porVendedor || [];
  const tabelaGeral  = data.tabelaGeral || [];
  const detalhado    = data.detalhado || [];
  const mixTipos     = data.mixTipos || [];
  const mixTipoVendedor = data.mixTipoVendedor || [];

  const semDados = (porVendedor.length === 0);

  // ── Excel ───────────────────────────────────────────────────────────────
  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    const sheet = [];
    sheet.push(['Relatório de Comissões de Serviço']);
    sheet.push([]);
    sheet.push(['Resumo Geral por Vendedor']);
    sheet.push(['Vendedor', 'Comissão Abertura', 'Comissão Fechamento', 'Total']);
    tabelaGeral.forEach((r) => sheet.push([r.vendedor, r.abertura, r.fechamento, r.total]));
    sheet.push([]);
    detalhado.forEach((v) => {
      sheet.push([`Vendedor: ${v.vendedor}`]);
      sheet.push(['Cliente', 'Tipo de Serviço', 'Comissão Abertura', 'Comissão Fechamento', 'Total', 'Data da Venda']);
      v.linhas.forEach((l) => sheet.push([l.cliente, l.servico, l.abertura, l.fechamento, l.total, l.data]));
      sheet.push([]);
    });
    const ws = XLSX.utils.aoa_to_sheet(sheet);
    XLSX.utils.book_append_sheet(wb, ws, 'Comissões Serviço');
    XLSX.writeFile(wb, 'relatorio-comissoes-servico.xlsx');
  };

  // altura do ranking proporcional ao nº de vendedores
  const alturaRanking = Math.max(160, porVendedor.length * 42 + 40);

  return (
    <Box mt={4}>
      <Stack direction="row" justifyContent="flex-end" gap={2} sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={() => setOpenFiltro(true)}>Filtro</Button>
        <Button variant="outlined" color="secondary" onClick={() => generatePdfRelatorioComissaoServico(data, filtro)}>
          Exportar em PDF
        </Button>
        <Button variant="contained" sx={{ backgroundColor: LARANJA, '&:hover': { backgroundColor: LARANJA2 } }} onClick={exportarExcel}>
          Download Excel
        </Button>
      </Stack>

      {semDados && (
        <Typography variant="body2" color="text.secondary" sx={{ my: 4 }}>
          Nenhuma comissão de serviço lançada no período selecionado.
        </Typography>
      )}

      {!semDados && (
        <>
          {/* ── KPIs ─────────────────────────────────────────────────────── */}
          <Stack direction="row" gap={1.5} flexWrap="wrap" mb={1}>
            <KpiCard label="Total de Comissões" valor={fmtBRL(kpis.total)} destaque />
            <KpiCard label="Vendedores"         valor={String(kpis.qtdVendedores)} />
            <KpiCard label="Vendas"             valor={String(kpis.qtdVendas)} />
            <KpiCard label="Ticket Médio"       valor={fmtBRL(kpis.ticketMedio)} />
          </Stack>

          {/* ── Gráficos ─────────────────────────────────────────────────── */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SecTitle>Comissão por Tipo de Serviço</SecTitle>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={porTipoServico} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {porTipoServico.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtBRL(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <SecTitle>Comissão por Vendedor</SecTitle>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={porVendedor} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {porVendedor.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtBRL(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>

          {/* ── Ranking (barras horizontais) ─────────────────────────────── */}
          <SecTitle>Ranking de Comissões por Vendedor</SecTitle>
          <ResponsiveContainer width="100%" height={alturaRanking}>
            <BarChart data={porVendedor} layout="vertical" margin={{ left: 20, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `R$ ${Number(v).toLocaleString('pt-BR')}`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
              <Tooltip formatter={(v) => fmtBRL(v)} />
              <Bar dataKey="value" name="Comissão" radius={[0, 4, 4, 0]}>
                {porVendedor.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* ── Mix de tipo de serviço por vendedor (barras empilhadas) ──── */}
          {mixTipos.length > 0 && mixTipoVendedor.length > 0 && (
            <>
              <SecTitle>Mix de Tipo de Serviço por Vendedor</SecTitle>
              <ResponsiveContainer width="100%" height={Math.max(220, mixTipoVendedor.length * 48 + 60)}>
                <BarChart data={mixTipoVendedor} margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="vendedor" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `R$ ${Number(v).toLocaleString('pt-BR')}`} />
                  <Tooltip formatter={(v) => fmtBRL(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {mixTipos.map((t, i) => (
                    <Bar key={t} dataKey={t} stackId="mix" name={t} fill={CORES[i % CORES.length]} radius={[2, 2, 0, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* ── Tabela geral por vendedor ────────────────────────────────── */}
          <SecTitle>Resumo Geral por Vendedor</SecTitle>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Vendedor</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Comissão Abertura</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Comissão Fechamento</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tabelaGeral.map((r, i) => (
                <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF8F0' } }}>
                  <TableCell>{r.vendedor}</TableCell>
                  <TableCell align="right">{fmtBRL(r.abertura)}</TableCell>
                  <TableCell align="right">{fmtBRL(r.fechamento)}</TableCell>
                  <TableCell align="right"><strong>{fmtBRL(r.total)}</strong></TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell><strong>Total Geral</strong></TableCell>
                <TableCell align="right"><strong>{fmtBRL(tabelaGeral.reduce((s, r) => s + r.abertura, 0))}</strong></TableCell>
                <TableCell align="right"><strong>{fmtBRL(tabelaGeral.reduce((s, r) => s + r.fechamento, 0))}</strong></TableCell>
                <TableCell align="right"><strong>{fmtBRL(tabelaGeral.reduce((s, r) => s + r.total, 0))}</strong></TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Divider sx={{ my: 3 }} />

          {/* ── Detalhado por vendedor ───────────────────────────────────── */}
          <SecTitle>Detalhamento por Vendedor</SecTitle>
          {detalhado.map((v, idx) => (
            <Box key={idx} mb={4} pl={1} borderLeft={`3px solid ${CORES[idx % CORES.length]}`}>
              <Typography variant="body2" fontWeight="bold" color={CINZA} sx={{ pl: 1, mb: 1 }}>
                {v.vendedor} — {fmtBRL(v.total)}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tipo de Serviço</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Comissão Abertura</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Comissão Fechamento</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data da Venda</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {v.linhas.map((l, i) => (
                    <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF8F0' } }}>
                      <TableCell>{l.cliente}</TableCell>
                      <TableCell>{l.servico}</TableCell>
                      <TableCell align="right">{fmtBRL(l.abertura)}</TableCell>
                      <TableCell align="right">{fmtBRL(l.fechamento)}</TableCell>
                      <TableCell align="right"><strong>{fmtBRL(l.total)}</strong></TableCell>
                      <TableCell>{l.data}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}
        </>
      )}

      {openFiltro && (
        <PopoverFiltroRelatorio
          filtro={filtro}
          open={openFiltro}
          onClose={onCloseFiltro}
          onLimpar={limparFiltro}
          onConfirm={onConfirmFiltro}
          type={type}
        />
      )}
    </Box>
  );
}